import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixEcomOrdersV1OrdersUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/ecom-orders',
        destPath: '',
      },
      {
        srcPath: '/_api/orders',
        destPath: '',
      },
      {
        srcPath: '/v1/orders',
        destPath: '/v1/orders',
      },
      {
        srcPath: '/ecom/v1/bulk/orders',
        destPath: '/v1/bulk/orders',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/ecom-orders',
        destPath: '',
      },
      {
        srcPath: '/_api/ep-orders',
        destPath: '/api',
      },
      {
        srcPath: '/ecom/v1/ep-orders',
        destPath: '/v1/orders',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/ecom/v1/bulk/orders',
        destPath: '/v1/bulk/orders',
      },
      {
        srcPath: '/ecom/v1/orders',
        destPath: '/v1/orders',
      },
      {
        srcPath: '/ecom/v1/ep-orders',
        destPath: '/v1/orders',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/ecom/v1/bulk/orders',
        destPath: '/v1/bulk/orders',
      },
      {
        srcPath: '/ecom/v1/orders',
        destPath: '/v1/orders',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

function resolveComWixEcomOrdersPaymentsCollectorV1PaymentsCollectorUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/payments-collector',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/ecom/v1/payments-collector',
        destPath: '/v1/payments-collector',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/ecom/v1/payments-collector',
        destPath: '/v1/payments-collector',
      },
      {
        srcPath: '/_api/v1/payments-collector',
        destPath: '/v1/payments-collector',
      },
      {
        srcPath: '/v1/payments-collector',
        destPath: '/v1/payments-collector',
      },
      {
        srcPath: '/_api/payments-collector',
        destPath: '/v1/payments-collector',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

function resolveComWixEcomOrdersImporterV1OrderImportsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {};

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_orders';

/**
 * Imports a single order with historical data while preserving original details and controlling side effects.
 * Can be used for initial import or update (delete-insert).
 * Only imported via ImportOrder API orders can be updated, Order.id used as key for update.
 * Order will be fully overridden.
 */
export function importOrder(payload: object): RequestOptionsFactory<any> {
  function __importOrder({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'order.createdDate' },
          { path: 'order.updatedDate' },
          { path: 'order.purchasedDate' },
          { path: 'order.lineItems.image.urlExpirationDate' },
          { path: 'order.lineItems.digitalFile.expirationDate' },
          {
            path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
          },
          { path: 'order.shippingInfo.logistics.deliverByDate' },
          { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
          { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
          { path: 'order.activities.createdDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'order.lineItems.image.focalPoint.x' },
          { path: 'order.lineItems.image.focalPoint.y' },
          { path: 'order.lineItems.physicalProperties.weight' },
          { path: 'order.billingInfo.address.geocode.latitude' },
          { path: 'order.billingInfo.address.geocode.longitude' },
          {
            path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
          },
          {
            path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
          },
          {
            path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
          },
          {
            path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
          },
          { path: 'order.recipientInfo.address.geocode.latitude' },
          { path: 'order.recipientInfo.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.importer.v1.OrderImports.ImportOrder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersImporterV1OrderImportsUrl({
        protoPath: '/v1/orders/import',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.purchasedDate' },
              { path: 'order.lineItems.image.urlExpirationDate' },
              { path: 'order.lineItems.digitalFile.expirationDate' },
              {
                path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'order.shippingInfo.logistics.deliverByDate' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'order.lineItems.image.focalPoint.x' },
              { path: 'order.lineItems.image.focalPoint.y' },
              { path: 'order.lineItems.physicalProperties.weight' },
              { path: 'order.billingInfo.address.geocode.latitude' },
              { path: 'order.billingInfo.address.geocode.longitude' },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'order.recipientInfo.address.geocode.latitude' },
              { path: 'order.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __importOrder;
}

/**
 * Sets the current order increment ID for a site. This defines the starting number
 * for subsequent new orders created after migration.
 */
export function setOrderNumberCounter(
  payload: object
): RequestOptionsFactory<any> {
  function __setOrderNumberCounter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.importer.v1.OrderImports.SetOrderNumberCounter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersImporterV1OrderImportsUrl({
        protoPath: '/v1/orders/import/set-order-number-counter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __setOrderNumberCounter;
}

/**
 * Bulk deletes previously imported orders. Only works on orders
 * that were created through the import process.
 */
export function bulkDeleteImportedOrders(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteImportedOrders({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.importer.v1.OrderImports.BulkDeleteImportedOrders',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersImporterV1OrderImportsUrl({
        protoPath: '/v1/orders/import/bulk-delete-imported-orders',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteImportedOrders;
}

/**
 * Prepares payment collection for given ecom order. This is the first of 2-step process of payment collection.
 * Here we ensure that payment collection is possible for given order and store and prepare payment gateway order for future charge.
 * 2nd step is an actual charge of prepared payment gateway order. This could be done either
 * via Wix-Cashier's API (https://bo.wix.com/wix-docs/rest/wix-cashier/pay/charge/charge-for-order)
 * or using Cashier Payments Widget (https://github.com/wix-private/cashier-client/tree/master/packages/cashier-payments-widget)
 */
export function preparePaymentCollection(
  payload: object
): RequestOptionsFactory<any> {
  function __preparePaymentCollection({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.payments_collector.v1.PaymentsCollector.PreparePaymentCollection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersPaymentsCollectorV1PaymentsCollectorUrl({
        protoPath:
          '/v1/payments-collector/orders/{ecomOrderId}/prepare-payment-collection',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __preparePaymentCollection;
}

/**
 * Provides payment collectability status for given order. If payment collection is possible
 * response will contain collectable amount for given ecom order. If not - response will contain
 * reason why payment collection is not possible.
 */
export function getPaymentCollectabilityStatus(
  payload: object
): RequestOptionsFactory<any> {
  function __getPaymentCollectabilityStatus({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'GET' as any,
      methodFqn:
        'com.wix.ecom.orders.payments_collector.v1.PaymentsCollector.GetPaymentCollectabilityStatus',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersPaymentsCollectorV1PaymentsCollectorUrl({
        protoPath:
          '/v1/payments-collector/orders/{ecomOrderId}/payment-collectability-status',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getPaymentCollectabilityStatus;
}

/**
 * Records and approves new manual payment with provided custom amount on given order.
 * Existing pending payments are ignored.
 * Custom amount is expected to be less or equal remaining amount to be paid on order (affected by approved payments, refunds and gift card payments)
 */
export function recordManuallyCollectedPayment(
  payload: object
): RequestOptionsFactory<any> {
  function __recordManuallyCollectedPayment({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.payments_collector.v1.PaymentsCollector.RecordManuallyCollectedPayment',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersPaymentsCollectorV1PaymentsCollectorUrl({
        protoPath: '/v1/payments-collector/orders/{orderId}/manual-payment',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __recordManuallyCollectedPayment;
}

/**
 * Marks the order as paid. `order.paymentStatus` field *eventually* changes to `PAID`.
 * In case the order already has an offline payment transaction associated with it
 * (usually when manual payment method is chosen at checkout) - This transaction will become approved.
 * In case the order has no payment transactions associated with it
 * (usually when the item is set to be paid offline after checkout or when an order is created from the backoffice) - A payment transaction
 * will be created and approved.
 */
export function paymentCollectionMarkOrderAsPaid(
  payload: object
): RequestOptionsFactory<any> {
  function __paymentCollectionMarkOrderAsPaid({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.payments_collector.v1.PaymentsCollector.MarkOrderAsPaid',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersPaymentsCollectorV1PaymentsCollectorUrl({
        protoPath: '/v1/payments-collector/orders/{ecomOrderId}/mark-as-paid',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.purchasedDate' },
              { path: 'order.lineItems.image.urlExpirationDate' },
              { path: 'order.lineItems.digitalFile.expirationDate' },
              {
                path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'order.shippingInfo.logistics.deliverByDate' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'order.lineItems.image.focalPoint.x' },
              { path: 'order.lineItems.image.focalPoint.y' },
              { path: 'order.lineItems.physicalProperties.weight' },
              { path: 'order.billingInfo.address.geocode.latitude' },
              { path: 'order.billingInfo.address.geocode.longitude' },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'order.recipientInfo.address.geocode.latitude' },
              { path: 'order.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __paymentCollectionMarkOrderAsPaid;
}

/** Marks multiple orders as paid. `order.paymentStatus` field *eventually* changes to `PAID`. */
export function paymentCollectionBulkMarkOrdersAsPaid(
  payload: object
): RequestOptionsFactory<any> {
  function __paymentCollectionBulkMarkOrdersAsPaid({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.payments_collector.v1.PaymentsCollector.BulkMarkOrdersAsPaid',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersPaymentsCollectorV1PaymentsCollectorUrl({
        protoPath: '/v1/bulk/payments-collector/mark-as-paid',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.purchasedDate' },
              { path: 'results.item.lineItems.image.urlExpirationDate' },
              { path: 'results.item.lineItems.digitalFile.expirationDate' },
              {
                path: 'results.item.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'results.item.shippingInfo.logistics.deliverByDate' },
              {
                path: 'results.item.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'results.item.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              { path: 'results.item.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.lineItems.image.focalPoint.x' },
              { path: 'results.item.lineItems.image.focalPoint.y' },
              { path: 'results.item.lineItems.physicalProperties.weight' },
              { path: 'results.item.billingInfo.address.geocode.latitude' },
              { path: 'results.item.billingInfo.address.geocode.longitude' },
              {
                path: 'results.item.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'results.item.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'results.item.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'results.item.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'results.item.recipientInfo.address.geocode.latitude' },
              { path: 'results.item.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __paymentCollectionBulkMarkOrdersAsPaid;
}

/** Checks whether this order can be refunded. */
export function getRefundabilityStatus(
  payload: object
): RequestOptionsFactory<any> {
  function __getRefundabilityStatus({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'GET' as any,
      methodFqn:
        'com.wix.ecom.orders.payments_collector.v1.PaymentsCollector.GetRefundabilityStatus',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersPaymentsCollectorV1PaymentsCollectorUrl({
        protoPath: '/v1/payments-collector/orders/{ecomOrderId}/refundability',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getRefundabilityStatus;
}

/**
 * Call this endpoint to create an order in the payment gateway system. The amount of the order would be either:
 * 1. An explicit amount provided in the request, or;
 * 2. If an explicit amount is not provided - the remaining amount to complete the payment of that eCom order.
 * As a result, an ID of the created payment gateway order would be returned.
 * You can then use Wix Payments APIs to approve that order or collect payment, which will eventually change the eCom order state (e.g mark it as paid).
 */
export function paymentCollectionCreatePaymentGatewayOrder(
  payload: object
): RequestOptionsFactory<any> {
  function __paymentCollectionCreatePaymentGatewayOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.payments_collector.v1.PaymentsCollector.CreatePaymentGatewayOrder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersPaymentsCollectorV1PaymentsCollectorUrl({
        protoPath:
          '/v1/payments-collector/orders/{ecomOrderId}/create-payment-gateway-order',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __paymentCollectionCreatePaymentGatewayOrder;
}

export function chargeMemberships(payload: object): RequestOptionsFactory<any> {
  function __chargeMemberships({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'membershipCharges.serviceProperties.scheduledDate' },
          { path: 'membershipCharges.serviceProperties.endDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.payments_collector.v1.PaymentsCollector.ChargeMemberships',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersPaymentsCollectorV1PaymentsCollectorUrl({
        protoPath:
          '/v1/payments-collector/orders/{ecomOrderId}/charge-membership',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __chargeMemberships;
}

/**
 * Calls corresponding payment providers and creates refund transactions for requested payments.
 * Updates order transactions based on refund results.
 * For requested payments with `TriggerRefundRequest.payments.external_refund = true` will not call payment providers and
 * will only update order transactions.
 */
export function triggerRefund(payload: object): RequestOptionsFactory<any> {
  function __triggerRefund({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.payments_collector.v1.PaymentsCollector.TriggerRefund',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersPaymentsCollectorV1PaymentsCollectorUrl({
        protoPath: '/v1/payments-collector/orders/{ecomOrderId}/trigger-refund',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'orderTransactions.payments.createdDate' },
              { path: 'orderTransactions.payments.updatedDate' },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.authorizedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.captures.createdDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.void.voidedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.scheduledAction.executionDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.chargebacks.createdDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.chargebacks.updatedDate',
              },
              { path: 'orderTransactions.refunds.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __triggerRefund;
}

/**
 * Void authorized payments
 * Transaction statuses for related payments will be updated in async way
 */
export function voidAuthorizedPayments(
  payload: object
): RequestOptionsFactory<any> {
  function __voidAuthorizedPayments({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.payments_collector.v1.PaymentsCollector.VoidAuthorizedPayments',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersPaymentsCollectorV1PaymentsCollectorUrl({
        protoPath:
          '/v1/payments-collector/orders/{ecomOrderId}/void-authorized-payments',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'orderTransactions.payments.createdDate' },
              { path: 'orderTransactions.payments.updatedDate' },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.authorizedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.captures.createdDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.void.voidedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.scheduledAction.executionDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.chargebacks.createdDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.chargebacks.updatedDate',
              },
              { path: 'orderTransactions.refunds.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __voidAuthorizedPayments;
}

/**
 * Capture authorized payments
 * Transaction statuses for related payments will be updated in async way
 */
export function captureAuthorizedPayments(
  payload: object
): RequestOptionsFactory<any> {
  function __captureAuthorizedPayments({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.payments_collector.v1.PaymentsCollector.CaptureAuthorizedPayments',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersPaymentsCollectorV1PaymentsCollectorUrl({
        protoPath:
          '/v1/payments-collector/orders/{ecomOrderId}/capture-authorized-payments',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'orderTransactions.payments.createdDate' },
              { path: 'orderTransactions.payments.updatedDate' },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.authorizedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.captures.createdDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.void.voidedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.scheduledAction.executionDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.chargebacks.createdDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.chargebacks.updatedDate',
              },
              { path: 'orderTransactions.refunds.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __captureAuthorizedPayments;
}

/**
 * Retrieves an order.
 *
 *
 * The `getOrder()` function returns a Promise that resolves when the specified order is retrieved.
 *
 * To retrieve an order's payment and refund details, including amounts, payment methods, and payment statuses, pass the order ID to [`listTransactionsForSingleOrder( )`](https://www.wix.com/velo/reference/wix-ecom-backend/ordertransactions/listtransactionsforsingleorder).
 */
export function getOrder(payload: object): RequestOptionsFactory<any> {
  function __getOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'GET' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.GetOrder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/orders/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.purchasedDate' },
              { path: 'order.lineItems.image.urlExpirationDate' },
              { path: 'order.lineItems.digitalFile.expirationDate' },
              {
                path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'order.shippingInfo.logistics.deliverByDate' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'order.lineItems.image.focalPoint.x' },
              { path: 'order.lineItems.image.focalPoint.y' },
              { path: 'order.lineItems.physicalProperties.weight' },
              { path: 'order.billingInfo.address.geocode.latitude' },
              { path: 'order.billingInfo.address.geocode.longitude' },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'order.recipientInfo.address.geocode.latitude' },
              { path: 'order.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getOrder;
}

/**
 * Retrieves a list of orders, given the provided paging, filtering, and sorting.
 *
 *
 * Search Orders runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order.
 * - `cursorPaging.limit` is `100`.
 *
 * > **Note:** Search Orders does not return orders with `status: "INITIALIZED"`, regardless of the filter applied. To retrieve an order with `status: "INITIALIZED"`, use [Get Order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/orders/get-order).
 *
 * To learn how to query or search orders, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
 */
export function searchOrders(payload: object): RequestOptionsFactory<any> {
  function __searchOrders({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.SearchOrders',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/orders/search',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'orders.createdDate' },
              { path: 'orders.updatedDate' },
              { path: 'orders.purchasedDate' },
              { path: 'orders.lineItems.image.urlExpirationDate' },
              { path: 'orders.lineItems.digitalFile.expirationDate' },
              {
                path: 'orders.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'orders.shippingInfo.logistics.deliverByDate' },
              { path: 'orders.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'orders.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'orders.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'orders.lineItems.image.focalPoint.x' },
              { path: 'orders.lineItems.image.focalPoint.y' },
              { path: 'orders.lineItems.physicalProperties.weight' },
              { path: 'orders.billingInfo.address.geocode.latitude' },
              { path: 'orders.billingInfo.address.geocode.longitude' },
              {
                path: 'orders.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'orders.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'orders.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'orders.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'orders.recipientInfo.address.geocode.latitude' },
              { path: 'orders.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __searchOrders;
}

/**
 * Creates an order.
 *
 *
 * The `createOrder()` function returns a Promise that resolves when the order is created.
 *
 * > **Notes:**
 * > + If an item is digital - `lineItems[i].itemType.preset: DIGITAL` - then `lineItems[i].digitalFile` must be provided.
 * > + If `lineItems[i].id` is passed, it must be either a valid GUID, or empty.
 */
export function createOrder(payload: object): RequestOptionsFactory<any> {
  function __createOrder({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'order.createdDate' },
          { path: 'order.updatedDate' },
          { path: 'order.purchasedDate' },
          { path: 'order.lineItems.image.urlExpirationDate' },
          { path: 'order.lineItems.digitalFile.expirationDate' },
          {
            path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
          },
          { path: 'order.shippingInfo.logistics.deliverByDate' },
          { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
          { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
          { path: 'order.activities.createdDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'order.lineItems.image.focalPoint.x' },
          { path: 'order.lineItems.image.focalPoint.y' },
          { path: 'order.lineItems.physicalProperties.weight' },
          { path: 'order.billingInfo.address.geocode.latitude' },
          { path: 'order.billingInfo.address.geocode.longitude' },
          {
            path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
          },
          {
            path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
          },
          {
            path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
          },
          {
            path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
          },
          { path: 'order.recipientInfo.address.geocode.latitude' },
          { path: 'order.recipientInfo.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.CreateOrder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/orders',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.purchasedDate' },
              { path: 'order.lineItems.image.urlExpirationDate' },
              { path: 'order.lineItems.digitalFile.expirationDate' },
              {
                path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'order.shippingInfo.logistics.deliverByDate' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'order.lineItems.image.focalPoint.x' },
              { path: 'order.lineItems.image.focalPoint.y' },
              { path: 'order.lineItems.physicalProperties.weight' },
              { path: 'order.billingInfo.address.geocode.latitude' },
              { path: 'order.billingInfo.address.geocode.longitude' },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'order.recipientInfo.address.geocode.latitude' },
              { path: 'order.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createOrder;
}

/**
 * Updates an order.
 *
 *
 * The `updateOrder()` function returns a Promise that resolves when the specified order's information is updated.
 *
 * Currently, the following fields can be updated:
 * + `order.buyerInfo.email`
 * + `order.buyerLanguage`
 * + `order.weightUnit`
 * + `order.billingInfo.address`
 * + `order.billingInfo.contactDetails`
 * + `order.archived`
 * + `order.attributionSource`
 * + `order.seenByAHuman`
 * + `order.recipientInfo.address`
 * + `order.recipientInfo.contactDetails`
 * + `order.shippingInfo.logistics.shippingDestination.address`
 * + `order.shippingInfo.logistics.shippingDestination.contactDetails`
 *
 * To update a field's value, include the new value in the `order` object in the method parameters.
 * To remove a field's value, pass `null`.
 *
 * > **Note:** Removing `buyerInfo` or `contactDetails` fields results in an error.
 *
 * To update an order's payment status, use [`updatePaymentStatus( )`](https://www.wix.com/velo/reference/wix-ecom-backend/ordertransactions/updatepaymentstatus).
 */
export function updateOrder(payload: object): RequestOptionsFactory<any> {
  function __updateOrder({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'order.createdDate' },
          { path: 'order.updatedDate' },
          { path: 'order.purchasedDate' },
          { path: 'order.lineItems.image.urlExpirationDate' },
          { path: 'order.lineItems.digitalFile.expirationDate' },
          {
            path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
          },
          { path: 'order.shippingInfo.logistics.deliverByDate' },
          { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
          { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
          { path: 'order.activities.createdDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'order.lineItems.image.focalPoint.x' },
          { path: 'order.lineItems.image.focalPoint.y' },
          { path: 'order.lineItems.physicalProperties.weight' },
          { path: 'order.billingInfo.address.geocode.latitude' },
          { path: 'order.billingInfo.address.geocode.longitude' },
          {
            path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
          },
          {
            path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
          },
          {
            path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
          },
          {
            path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
          },
          { path: 'order.recipientInfo.address.geocode.latitude' },
          { path: 'order.recipientInfo.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'PATCH' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.UpdateOrder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/orders/{order.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.purchasedDate' },
              { path: 'order.lineItems.image.urlExpirationDate' },
              { path: 'order.lineItems.digitalFile.expirationDate' },
              {
                path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'order.shippingInfo.logistics.deliverByDate' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'order.lineItems.image.focalPoint.x' },
              { path: 'order.lineItems.image.focalPoint.y' },
              { path: 'order.lineItems.physicalProperties.weight' },
              { path: 'order.billingInfo.address.geocode.latitude' },
              { path: 'order.billingInfo.address.geocode.longitude' },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'order.recipientInfo.address.geocode.latitude' },
              { path: 'order.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateOrder;
}

/**
 * Updates up to 100 orders.
 *
 *
 * The `bulkUpdateOrders()` function returns a Promise that resolves when the specified orders' information is updated.
 *
 * Currently, the following fields can be updated:
 * + `order.buyerInfo.email`
 * + `order.buyerLanguage`
 * + `order.weightUnit`
 * + `order.billingInfo.address`
 * + `order.billingInfo.contactDetails`
 * + `order.archived`
 * + `order.attributionSource`
 * + `order.seenByAHuman`
 * + `order.recipientInfo.address`
 * + `order.recipientInfo.contactDetails`
 * + `order.shippingInfo.logistics.shippingDestination.address`
 * + `order.shippingInfo.logistics.shippingDestination.contactDetails`
 *
 * To update a field's value, include the new value in the `orders.order` object in the method parameters.
 * To remove a field's value, pass `null`.
 *
 * > **Note:** Removing `buyerInfo` or `contactDetails` fields results in an error.
 *
 * To update an order's payment status, use [`updatePaymentStatus( )`](https://www.wix.com/velo/reference/wix-ecom-backend/ordertransactions/updatepaymentstatus).
 */
export function bulkUpdateOrders(payload: object): RequestOptionsFactory<any> {
  function __bulkUpdateOrders({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'orders.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'orders.order.createdDate' },
          { path: 'orders.order.updatedDate' },
          { path: 'orders.order.purchasedDate' },
          { path: 'orders.order.lineItems.image.urlExpirationDate' },
          { path: 'orders.order.lineItems.digitalFile.expirationDate' },
          {
            path: 'orders.order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
          },
          { path: 'orders.order.shippingInfo.logistics.deliverByDate' },
          { path: 'orders.order.shippingInfo.logistics.deliveryTimeSlot.from' },
          { path: 'orders.order.shippingInfo.logistics.deliveryTimeSlot.to' },
          { path: 'orders.order.activities.createdDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'orders.order.lineItems.image.focalPoint.x' },
          { path: 'orders.order.lineItems.image.focalPoint.y' },
          { path: 'orders.order.lineItems.physicalProperties.weight' },
          { path: 'orders.order.billingInfo.address.geocode.latitude' },
          { path: 'orders.order.billingInfo.address.geocode.longitude' },
          {
            path: 'orders.order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
          },
          {
            path: 'orders.order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
          },
          {
            path: 'orders.order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
          },
          {
            path: 'orders.order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
          },
          { path: 'orders.order.recipientInfo.address.geocode.latitude' },
          { path: 'orders.order.recipientInfo.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.BulkUpdateOrders',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/bulk/orders/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.purchasedDate' },
              { path: 'results.item.lineItems.image.urlExpirationDate' },
              { path: 'results.item.lineItems.digitalFile.expirationDate' },
              {
                path: 'results.item.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'results.item.shippingInfo.logistics.deliverByDate' },
              {
                path: 'results.item.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'results.item.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              { path: 'results.item.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.lineItems.image.focalPoint.x' },
              { path: 'results.item.lineItems.image.focalPoint.y' },
              { path: 'results.item.lineItems.physicalProperties.weight' },
              { path: 'results.item.billingInfo.address.geocode.latitude' },
              { path: 'results.item.billingInfo.address.geocode.longitude' },
              {
                path: 'results.item.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'results.item.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'results.item.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'results.item.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'results.item.recipientInfo.address.geocode.latitude' },
              { path: 'results.item.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateOrders;
}

/** @deprecated Will be removed on 2024-02-15. */
export function updateOrderLineItem(
  payload: object
): RequestOptionsFactory<any> {
  function __updateOrderLineItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'lineItem.image.urlExpirationDate' },
          { path: 'lineItem.digitalFile.expirationDate' },
          { path: 'lineItem.subscriptionInfo.subscriptionSettings.startDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'lineItem.image.focalPoint.x' },
          { path: 'lineItem.image.focalPoint.y' },
          { path: 'lineItem.physicalProperties.weight' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'PATCH' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.UpdateOrderLineItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/orders/{id}/line-item/{lineItem.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.purchasedDate' },
              { path: 'order.lineItems.image.urlExpirationDate' },
              { path: 'order.lineItems.digitalFile.expirationDate' },
              {
                path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'order.shippingInfo.logistics.deliverByDate' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'order.lineItems.image.focalPoint.x' },
              { path: 'order.lineItems.image.focalPoint.y' },
              { path: 'order.lineItems.physicalProperties.weight' },
              { path: 'order.billingInfo.address.geocode.latitude' },
              { path: 'order.billingInfo.address.geocode.longitude' },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'order.recipientInfo.address.geocode.latitude' },
              { path: 'order.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateOrderLineItem;
}

/**
 * Adds a merchant comment to an order.
 * > **Note:** Activities with type CustomActivity not supported
 * @deprecated It has been replaced with com.wix.ecom.orders.v1.OrdersService.AddActivities(), and will be removed on 2024-11-01.
 */
export function addActivity(payload: object): RequestOptionsFactory<any> {
  function __addActivity({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.AddActivity',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/orders/{id}/activities',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.purchasedDate' },
              { path: 'order.lineItems.image.urlExpirationDate' },
              { path: 'order.lineItems.digitalFile.expirationDate' },
              {
                path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'order.shippingInfo.logistics.deliverByDate' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'order.lineItems.image.focalPoint.x' },
              { path: 'order.lineItems.image.focalPoint.y' },
              { path: 'order.lineItems.physicalProperties.weight' },
              { path: 'order.billingInfo.address.geocode.latitude' },
              { path: 'order.billingInfo.address.geocode.longitude' },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'order.recipientInfo.address.geocode.latitude' },
              { path: 'order.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addActivity;
}

/**
 * Adds up to 300 activities to an order in a single request.
 *
 *
 * An order activity is a record of an action taken on an order. Use this endpoint to add merchant comments or record order-related events.
 */
export function addActivities(payload: object): RequestOptionsFactory<any> {
  function __addActivities({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'orderActivities.createdDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.AddActivities',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/orders/{orderId}/activities/add',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.purchasedDate' },
              { path: 'order.lineItems.image.urlExpirationDate' },
              { path: 'order.lineItems.digitalFile.expirationDate' },
              {
                path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'order.shippingInfo.logistics.deliverByDate' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'order.lineItems.image.focalPoint.x' },
              { path: 'order.lineItems.image.focalPoint.y' },
              { path: 'order.lineItems.physicalProperties.weight' },
              { path: 'order.billingInfo.address.geocode.latitude' },
              { path: 'order.billingInfo.address.geocode.longitude' },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'order.recipientInfo.address.geocode.latitude' },
              { path: 'order.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addActivities;
}

/** Updates an order's activity. */
export function updateActivity(payload: object): RequestOptionsFactory<any> {
  function __updateActivity({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'PATCH' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.UpdateActivity',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/orders/{id}/activities/{activityId}',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.purchasedDate' },
              { path: 'order.lineItems.image.urlExpirationDate' },
              { path: 'order.lineItems.digitalFile.expirationDate' },
              {
                path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'order.shippingInfo.logistics.deliverByDate' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'order.lineItems.image.focalPoint.x' },
              { path: 'order.lineItems.image.focalPoint.y' },
              { path: 'order.lineItems.physicalProperties.weight' },
              { path: 'order.billingInfo.address.geocode.latitude' },
              { path: 'order.billingInfo.address.geocode.longitude' },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'order.recipientInfo.address.geocode.latitude' },
              { path: 'order.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateActivity;
}

/**
 * Delete's an order's activity.
 *
 * Only custom activities and merchant comments can be deleted.
 */
export function deleteActivity(payload: object): RequestOptionsFactory<any> {
  function __deleteActivity({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'DELETE' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.DeleteActivity',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/orders/{id}/activities/{activityId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.purchasedDate' },
              { path: 'order.lineItems.image.urlExpirationDate' },
              { path: 'order.lineItems.digitalFile.expirationDate' },
              {
                path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'order.shippingInfo.logistics.deliverByDate' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'order.lineItems.image.focalPoint.x' },
              { path: 'order.lineItems.image.focalPoint.y' },
              { path: 'order.lineItems.physicalProperties.weight' },
              { path: 'order.billingInfo.address.geocode.latitude' },
              { path: 'order.billingInfo.address.geocode.longitude' },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'order.recipientInfo.address.geocode.latitude' },
              { path: 'order.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __deleteActivity;
}

/**
 * Cancels an order.
 *
 *
 * The `cancelOrder()` function returns a Promise that resolves when the specified order is canceled and the `order.status` field changes to `CANCELED`.
 */
export function cancelOrder(payload: object): RequestOptionsFactory<any> {
  function __cancelOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.CancelOrder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/orders/{id}/cancel',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.purchasedDate' },
              { path: 'order.lineItems.image.urlExpirationDate' },
              { path: 'order.lineItems.digitalFile.expirationDate' },
              {
                path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'order.shippingInfo.logistics.deliverByDate' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'order.lineItems.image.focalPoint.x' },
              { path: 'order.lineItems.image.focalPoint.y' },
              { path: 'order.lineItems.physicalProperties.weight' },
              { path: 'order.billingInfo.address.geocode.latitude' },
              { path: 'order.billingInfo.address.geocode.longitude' },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'order.recipientInfo.address.geocode.latitude' },
              { path: 'order.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __cancelOrder;
}

/**
 * Updates order status.
 *
 * >CANCELED and REJECTED are final statuses. Update from either of those will fail.
 * >Updates into INITIALIZED are not supported.
 *
 * Approving order:
 * APPROVED status can be set while order has one of [INITIALIZED, PENDING] statuses.
 *
 * Approving an order which was created with CreateOrderRequest.settings.order_approval_strategy = PAYMENT_METHOD_SAVED
 * will cause an error.
 *
 * Setting PENDING status:
 * Only orders in INITIALIZED status can be updated into PENDING.
 */
export function updateOrderStatus(payload: object): RequestOptionsFactory<any> {
  function __updateOrderStatus({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.UpdateOrderStatus',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/orders/{orderId}/update-status',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.purchasedDate' },
              { path: 'order.lineItems.image.urlExpirationDate' },
              { path: 'order.lineItems.digitalFile.expirationDate' },
              {
                path: 'order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'order.shippingInfo.logistics.deliverByDate' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.from' },
              { path: 'order.shippingInfo.logistics.deliveryTimeSlot.to' },
              { path: 'order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'order.lineItems.image.focalPoint.x' },
              { path: 'order.lineItems.image.focalPoint.y' },
              { path: 'order.lineItems.physicalProperties.weight' },
              { path: 'order.billingInfo.address.geocode.latitude' },
              { path: 'order.billingInfo.address.geocode.longitude' },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'order.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'order.recipientInfo.address.geocode.latitude' },
              { path: 'order.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateOrderStatus;
}

export function aggregateOrders(payload: object): RequestOptionsFactory<any> {
  function __aggregateOrders({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.AggregateOrders',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/orders/aggregate',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __aggregateOrders;
}

/**
 * Synchronously update tags on multiple orders, by list of order IDs.
 *
 * > **Note:** If the same tag is passed to both the `assignTags` and `unassignTags` fields, it will be assigned.
 */
export function bulkUpdateOrderTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateOrderTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.v1.Orders.BulkUpdateOrderTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomOrdersV1OrdersUrl({
        protoPath: '/v1/bulk/orders/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateOrderTags;
}
